import { useState, useEffect } from "react";
// MUI
import {
  Alert,
  AlertTitle,
  Badge,
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { DateTime } from "luxon";

//TIME & DATE
import {
  LocalizationProvider,
  MobileDatePicker,
  PickersDay,
  pickersDayClasses,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

//COMPONENTS
import WaitlistInfo from "./WaitlistInfo";
import { PendingActionsOutlined } from "@mui/icons-material";

//Constants
import CustomButton from "../../constants/CustomButton";
import customHolidays from "../../constants/CustomHolidays";
import { checkboxStyles } from "./styles";
import { styles as mainStyles } from "../styles";
import { useNavigate } from "react-router";
import { darkBlue } from "../../constants/ColourConstants";
import { checkboxes, checkboxFields } from "../../constants/BookingConstants";

const MANITOBA_TIMEZONE = "America/Winnipeg";

const today = DateTime.now().setZone(MANITOBA_TIMEZONE).toFormat("yyyy-MM-dd");
const tomorrow = DateTime.now()
  .setZone(MANITOBA_TIMEZONE)
  .plus({ days: 1 })
  .toFormat("yyyy-MM-dd");

const FIVE_MINUTES = 300000;
const WAITLIST_START_TIME = DateTime.now()
  .setZone(MANITOBA_TIMEZONE)
  .set({ hour: 6, minute: 0, second: 0, millisecond: 0 });
const WAITLIST_END_TIME = DateTime.now()
  .setZone(MANITOBA_TIMEZONE)
  .set({ hour: 18, minute: 0, second: 0, millisecond: 0 });

const BookingForm = (props) => {
  const formik = props.formik;
  const highlightedDays = props.highlightedDays;
  const slots = props.slots;
  const isOnWaitlist = props.activeWaitlist;
  const refreshAppointments = props.refreshAppointments;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [holidayDates, setHolidayDates] = useState([]);
  const [isHoliday, setIsHoliday] = useState(false);

  const hasCheckboxError = checkboxFields.some(
    (name) => formik.touched[name] && Boolean(formik.errors[name])
  );
  const allowJoinWaitlist =
    formik.values.date === today &&
    DateTime.now().setZone(MANITOBA_TIMEZONE) >= WAITLIST_START_TIME &&
    DateTime.now().setZone(MANITOBA_TIMEZONE) < WAITLIST_END_TIME &&
    !isHoliday;
  const hasSlots = slots.length > 0;
  const showWaitlistAlert =
    !hasSlots && isOnWaitlist && formik.values.date === today;
  const showXrayWarning =
    formik.values.available &&
    formik.values.available.time &&
    DateTime.fromFormat(formik.values.available.time, "h:mm a").toFormat(
      "HH:mm"
    ) >=
      (DateTime.fromISO(formik.values.available.date).weekday === 1
        ? "19:00"
        : "18:15");

  useEffect(() => {
    const fetchHolidays = async () => {
      const currentYear = DateTime.now().setZone(MANITOBA_TIMEZONE).year;
      const response = await fetch(
        `https://canada-holidays.ca/api/v1/provinces/MB?year=${currentYear}`
      );
      const data = await response.json();
      const holidays = data.province.holidays.map((holiday) => holiday.date);

      const allHolidays = [...holidays, ...customHolidays];

      setHolidayDates(allHolidays);
    };
    fetchHolidays();
  }, []);

  useEffect(() => {
    if (formik.values.date) {
      const formattedDate = DateTime.fromISO(formik.values.date).toFormat(
        "yyyy-MM-dd"
      );
      const holidayStatus = holidayDates.includes(formattedDate);
      setIsHoliday(holidayStatus);
    }
  }, [formik.values.date, holidayDates]);

  useEffect(() => {
    const refreshInterval = setInterval(() => {
      refresh();
    }, FIVE_MINUTES);
    return () => {
      clearInterval(refreshInterval);
    };
  });

  const refresh = async () => {
    setLoading(true);
    formik.setFieldValue("available", "");
    formik.setFieldError("available", undefined);
    formik.setFieldTouched("available", false);
    await refreshAppointments();
    setLoading(false);
  };

  const handleDateChange = (newDate) => {
    formik.setFieldValue(
      "date",
      DateTime.fromJSDate(newDate.$d).toFormat("yyyy-MM-dd")
    );
    formik.setFieldValue("available", "");
  };

  return (
    <>
      <Typography sx={mainStyles.typographyTitle}>BOOK APPOINTMENT</Typography>
      <form className="form" onSubmit={formik.handleSubmit}>
        {/* Date, Time & Refresh Stack */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{
            mt: 2,
            justifyContent: "center",
            width: "100%",
          }}
        >
          {/* Date Picker */}
          <Stack sx={{ flex: 1, width: "100%" }}>
            <FormControl fullWidth variant="filled">
              <LocalizationProvider dateAdapter={AdapterDayjs} fullWidth>
                <MobileDatePicker
                  id="date"
                  name="date"
                  label="Select Appointment Date"
                  inputFormat="MM/DD/YYYY"
                  minDate={today}
                  maxDate={tomorrow}
                  disableHighlightToday
                  value={formik.values.date}
                  onChange={handleDateChange}
                  closeOnSelect
                  inputProps={{ readOnly: true }}
                  renderInput={(params) => (
                    <TextField
                      variant="filled"
                      {...params}
                      fullWidth
                      error={false}
                    />
                  )}
                  renderDay={(day, _value, DayComponentProps) => {
                    const isSelected = highlightedDays.some(
                      (date) =>
                        date.day === day.date() &&
                        date.month === day.month() + 1
                    );
                    return (
                      <Badge
                        key={day.toString()}
                        overlap="circular"
                        invisible={isSelected ? false : true}
                        sx={{
                          "& .MuiBadge-badge": { backgroundColor: darkBlue },
                        }}
                        variant="dot"
                      >
                        <PickersDay
                          {...DayComponentProps}
                          sx={{
                            [`&&.${pickersDayClasses.selected}`]: {
                              backgroundColor: darkBlue,
                            },
                          }}
                        />
                      </Badge>
                    );
                  }}
                />
                <FormHelperText
                  error={formik.touched.date && Boolean(formik.errors.date)}
                >
                  {formik.touched.date && formik.errors.date}
                </FormHelperText>
              </LocalizationProvider>
            </FormControl>
          </Stack>
          {/* Time Picker, Refresh Button and Error Helper Text */}
          <Stack
            direction="column"
            spacing={0}
            sx={{ flex: 1, justifyContent: "center", width: "100%" }}
          >
            <Stack direction="row" spacing={1}>
              <Stack sx={{ flex: 3 }}>
                <TextField
                  select
                  fullWidth
                  variant="filled"
                  label="Select Appointment Time"
                  name="available"
                  value={formik.values.available}
                  onChange={formik.handleChange}
                  disabled={!hasSlots}
                >
                  {slots.map((e, i) => (
                    <MenuItem key={i} value={e}>
                      {e.time}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
              <Stack
                sx={{ flex: 1, alignItems: "center", justifyContent: "center" }}
              >
                <Tooltip
                  enterTouchDelay={50}
                  title="Appointments are refreshed automatically every five minutes. Please refresh to get the most up-to-date appointments."
                  placement="top"
                  arrow
                >
                  <span>
                    <CustomButton
                      label="Refresh"
                      disabled={loading}
                      startIcon={
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {loading ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <RefreshIcon />
                          )}
                        </Box>
                      }
                      onClick={async () => {
                        await refresh();
                      }}
                    ></CustomButton>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>
            <Stack sx={{ my: 0, ml: "14px" }}>
              <FormHelperText
                error={
                  formik.touched.available && Boolean(formik.errors.available)
                }
              >
                {formik.touched.available && formik.errors.available}
              </FormHelperText>
            </Stack>
          </Stack>
        </Stack>

        {/* X-ray Warning Alert */}
        {showXrayWarning && (
          <Alert severity="warning" sx={mainStyles.alertBooking}>
            <AlertTitle sx={{ fontWeight: "bold" }}>
              X-ray may not be available during or after this time slot.
            </AlertTitle>
            If an X-ray is required, you may be asked to return for this test
            the following day. Please book accordingly.
          </Alert>
        )}

        {/* Waitlist Alert */}
        {showWaitlistAlert && (
          <Stack spacing={1}>
            <Alert
              severity="info"
              icon={<PendingActionsOutlined />}
              sx={mainStyles.alertBooking}
            >
              <AlertTitle sx={{ fontWeight: "bold" }}>
                You're currently on the waitlist!
              </AlertTitle>
              All appointment slots are booked for the selected date. We will
              contact you once a slot becomes available.
            </Alert>
            <WaitlistInfo />
          </Stack>
        )}

        {/* Available slots */}
        {hasSlots && (
          <>
            <TextField
              sx={{ my: 1 }}
              fullWidth
              id="notes"
              name="notes"
              type="text"
              label="Please provide your reason for booking this appointment."
              variant="filled"
              multiline
              rows={3}
              disabled={formik.values.reason === ""}
              value={formik.values.notes}
              onChange={formik.handleChange}
              error={formik.touched.notes && Boolean(formik.errors.notes)}
              helperText={formik.touched.notes && formik.errors.notes}
            />
            <Typography sx={mainStyles.leftAlignedTypographyBody}>
              I understand that:
            </Typography>
            <FormControl sx={checkboxStyles}>
              <FormGroup>
                {checkboxes.map(({ name, label }) => (
                  <FormControlLabel
                    key={name}
                    control={
                      <Checkbox
                        id={name}
                        name={name}
                        checked={formik.values[name]}
                        onChange={formik.handleChange}
                      />
                    }
                    label={label}
                  />
                ))}
              </FormGroup>
              {hasCheckboxError && (
                <FormHelperText error>
                  Please acknowledge all statements before continuing.
                </FormHelperText>
              )}
            </FormControl>
          </>
        )}

        {/* No Available Slots & Waitlist Option */}
        {!hasSlots &&
          formik.values.reason &&
          formik.values.date &&
          !showWaitlistAlert && (
            <Stack spacing={1} sx={{ pt: 1 }}>
              {allowJoinWaitlist ? (
                <>
                  <Alert severity="error" sx={mainStyles.alertBooking}>
                    <AlertTitle sx={{ fontWeight: "bold" }}>
                      All appointment slots are booked for the selected date.
                    </AlertTitle>
                    If you would like to be added to our one-day waitlist,
                    please specify your reason for appointment below.
                  </Alert>

                  <TextField
                    fullWidth
                    id="notes"
                    name="notes"
                    type="text"
                    label="Please provide your reason for booking this appointment."
                    variant="filled"
                    multiline
                    rows={3}
                    disabled={formik.values.reason === ""}
                    value={formik.values.notes}
                    onChange={formik.handleChange}
                    error={formik.touched.notes && Boolean(formik.errors.notes)}
                    helperText={formik.touched.notes && formik.errors.notes}
                  />
                  <WaitlistInfo />
                </>
              ) : (
                <Alert severity="warning" sx={mainStyles.alertBooking}>
                  <AlertTitle sx={{ m: 0, fontWeight: "bold" }}>
                    There are no available booking slots for the selected date.
                  </AlertTitle>
                </Alert>
              )}
            </Stack>
          )}

        {/* Buttons */}
        <Stack
          direction="row"
          sx={{
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            mt: 2,
          }}
          spacing={2}
        >
          <CustomButton
            label="Back"
            onClick={() => navigate("/")}
            type="secondary"
          />
          {formik.values.date && (
            <>
              {hasSlots ? (
                <CustomButton
                  label="Submit"
                  disabled={loading}
                  onClick={async () => {
                    await formik.setFieldValue("joinWaitlist", false);
                    formik.handleSubmit();
                  }}
                />
              ) : (
                <>
                  {allowJoinWaitlist && (
                    <CustomButton
                      label="Join Waitlist"
                      disabled={isOnWaitlist}
                      onClick={async () => {
                        await formik.setFieldValue("joinWaitlist", true);
                        formik.handleSubmit();
                      }}
                    />
                  )}
                </>
              )}
            </>
          )}
        </Stack>
      </form>
    </>
  );
};

export default BookingForm;
